import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { FileText, Shield, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const CGUAcceptanceModal = ({ onAccepted }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const handleAccept = async () => {
    if (!accepted || !acceptedPrivacy) {
      toast.error('Vous devez cocher les deux cases pour continuer');
      return;
    }

    setLoading(true);
    try {
      await api.post('/accept-cgu');
      toast.success('Conditions acceptees');
      onAccepted();
    } catch (error) {
      toast.error('Erreur lors de la validation');
      console.error('CGU acceptance error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <Card className="w-full max-w-lg bg-zinc-900 border-zinc-700 my-4">
        <CardHeader className="border-b border-zinc-700 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-full">
              <FileText className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Conditions d'utilisation</CardTitle>
              <p className="text-sm text-zinc-400 mt-1">
                Veuillez accepter pour continuer
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="py-4 space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-200">
                Pour utiliser StationCab, vous devez accepter nos conditions.
              </p>
            </div>
          </div>

          {/* Liens vers les documents */}
          <div className="space-y-2">
            <button
              onClick={() => openLink('https://stationcab.fr/cgv')}
              className="w-full flex items-center justify-between p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-left transition-colors"
            >
              <span className="text-white text-sm">Conditions Generales de Vente</span>
              <ExternalLink className="w-4 h-4 text-zinc-400" />
            </button>
            
            <button
              onClick={() => openLink('https://stationcab.fr/mentions-legales')}
              className="w-full flex items-center justify-between p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-left transition-colors"
            >
              <span className="text-white text-sm">Mentions Legales</span>
              <ExternalLink className="w-4 h-4 text-zinc-400" />
            </button>
            
            <button
              onClick={() => openLink('https://stationcab.fr/politique-confidentialite')}
              className="w-full flex items-center justify-between p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-left transition-colors"
            >
              <span className="text-white text-sm">Politique de Confidentialite</span>
              <ExternalLink className="w-4 h-4 text-zinc-400" />
            </button>
          </div>

          {/* Cases a cocher */}
          <div className="space-y-3 pt-2">
            <button
              onClick={() => setAccepted(!accepted)}
              className={`w-full flex items-center gap-3 p-4 rounded-lg text-left transition-colors ${
                accepted ? 'bg-green-500/20 border-2 border-green-500' : 'bg-zinc-800 border-2 border-zinc-700'
              }`}
            >
              <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                accepted ? 'bg-green-500' : 'bg-zinc-700'
              }`}>
                {accepted && <span className="text-white text-sm font-bold">✓</span>}
              </div>
              <span className="text-white text-sm">
                J'accepte les CGV et les Mentions Legales
              </span>
            </button>

            <button
              onClick={() => setAcceptedPrivacy(!acceptedPrivacy)}
              className={`w-full flex items-center gap-3 p-4 rounded-lg text-left transition-colors ${
                acceptedPrivacy ? 'bg-green-500/20 border-2 border-green-500' : 'bg-zinc-800 border-2 border-zinc-700'
              }`}
            >
              <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                acceptedPrivacy ? 'bg-green-500' : 'bg-zinc-700'
              }`}>
                {acceptedPrivacy && <span className="text-white text-sm font-bold">✓</span>}
              </div>
              <span className="text-white text-sm">
                J'accepte la Politique de Confidentialite
              </span>
            </button>
          </div>

          {/* Bouton accepter */}
          <Button
            onClick={handleAccept}
            disabled={!accepted || !acceptedPrivacy || loading}
            className="w-full h-14 text-lg mt-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Validation...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Accepter et continuer
              </span>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CGUAcceptanceModal;
